import React, { FC, useEffect } from "react";
import { useNavigate } from "react-router";
import { DateTime } from "luxon";
import "chart.js/auto";
import { Bar } from 'react-chartjs-2';

import "./dashboard.scss";
import CommonHeader from "../../shared/common-header/common-header";
import { useAppDispatch } from "../../app/hooks";
import { useAppSelector } from "../../app/hooks";
import { bookingActions, fetchBookings } from "../bookings/bookingSlice";
import {
  transactionActions,
  fetchTransactions,
  fetchMerchantTransactionRevenue,
  fetchMerchantRevenewGrowthRate,
  fetchMerchanPayoutBalance,
  fetchMerchantRevenueMonthlyClassification
} from "../transaction/transactionSlice";
import { capitalizeFullString, mapTimeZone } from "../../utils/common";
import { useTranslation } from "react-i18next";

interface DashboardProps { }

const Dashboard: FC<DashboardProps> = () => {
  useEffect(() => {
    const elem: any = document.getElementById("mainBodyWrapper");
    elem.className = "mainBodyWrapper whiteBg";
    return () => {
      elem.className = "mainBodyWrapper";
    };
  });

  useEffect(() => {
    dispatch(bookingActions.bookingList('?skip=0&limit=2&type=upcoming'))
    dispatch(transactionActions.fetchTransactions(`skip=0&limit=2`))
    dispatch(transactionActions.fetchDashboardStats())
  }, [])

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {t}=useTranslation("")

  const bookings: any = useAppSelector(fetchBookings);
  const transactions: any = useAppSelector(fetchTransactions);
  const merchantTransactionRevenue: number = useAppSelector(fetchMerchantTransactionRevenue);
  const merchantRevenewGrowthRate: number = useAppSelector(fetchMerchantRevenewGrowthRate);
  const merchanPayoutBalance: number = useAppSelector(fetchMerchanPayoutBalance);
  const merchantRevenueMonthlyClassification: Array<number> = useAppSelector(fetchMerchantRevenueMonthlyClassification);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: t("DashboardPage.Revenue"),
      },
    },
  };

  const labels = [t("DashboardPage.chartLabels.Jan"), t("DashboardPage.chartLabels.Feb"), t("DashboardPage.chartLabels.Mar"), t("DashboardPage.chartLabels.Apr"), t("DashboardPage.chartLabels.May"), t("DashboardPage.chartLabels.Jun"), t("DashboardPage.chartLabels.Jul"), t("DashboardPage.chartLabels.Aug"), t("DashboardPage.chartLabels.Sep"), t("DashboardPage.chartLabels.Oct"), t("DashboardPage.chartLabels.Nov"), t("DashboardPage.chartLabels.Dec")];

  const data = {
    labels,
    datasets: [
      {
        label: 'Monthly',
        data: merchantRevenueMonthlyClassification?.length > 0 ? [...merchantRevenueMonthlyClassification] : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],  // cannot directly pass the value since charts package deletes some values which is allowed only in [] not Array()
        backgroundColor: 'rgb(0,209,159)',
      }
    ],
  };

  return (
    <div className="dashboardWrapper">
      <CommonHeader sectionTitle={t("headerComponent.Dashboard")} hasRatingVisible={false}></CommonHeader>
      <div className="dashboardInner">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="card mb-3">
                <div className="card-body">
                  <div className="row gutters-10">
                    <div className="col-12 col-md-6 col-lg-5 col-xl-4">
                      <div className="detailSection">
                        <div className="_section">
                          <label className="_total fontsize-16 mb-1">
                            {t("DashboardPage.TotalRevenue")}
                          </label>
                          <h2 className="d-flex align-items-center flex-wrap">
                            <span className="me-2">{merchantTransactionRevenue ? `$` + merchantTransactionRevenue.toLocaleString() : '$0.00'}</span>{" "}
                          </h2>

                        </div>
                        <div className="_section mt-4">
                          <label className="fontsize-12 mb-2">
                          {t("DashboardPage.PayoutBalance")}
                          </label>
                          <h5 className="mb-4"> {merchanPayoutBalance ? `$` + merchanPayoutBalance.toLocaleString() : '$0.00'} </h5>
                          <div className="row gutters-5 align-items-center">
                            <div className="col">
                              <p className="mb-0 fontsize-12">
                                {/* Next Payout: July 31, 2023 */}
                              </p>
                            </div>
                            {/* <div className="col-auto">
                            <a href={void 0} className="fw700 color-active">
                              View All
                            </a>
                          </div> */}
                          </div>
                        </div>

                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-7 col-xl-8">
                      <Bar options={options} height={250} data={data} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-lg-6">
              <div className="card mb-3">
                <div className="card-header">
                  <div className="row gutters-10">
                    <div className="col">
                      <h4 className="card-title mb-0">{t("DashboardPage.UpcomingBookings")}</h4>
                    </div>
                    <div className="col-auto">
                      <a href={void 0} onClick={() => { navigate("/bookings/upcoming") }} className="fw700 color-active">
                      {t("DashboardPage.ViewAll")}
                      </a>
                    </div>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-border">
                      <thead>
                        <tr>
                          <th>{t("DashboardPage.TableHeading.NameOrEmail")}</th>
                          <th>{t("DashboardPage.TableHeading.TeeTime")}</th>
                          <th>{t("DashboardPage.TableHeading.NumberOfGolfers")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings && bookings.length > 0 && bookings.map((booking: any, index: number) => (
                          <tr key={index}>
                            <td>
                              <div className="userInfo d-flex align-items-center">
                                <span className="userImage me-2">
                                  {!(booking?.user?.profileImage) && <img
                                    src="./assets/images/default-user.png"
                                    alt=""
                                    className="img-fluid"
                                  />}
                                  {booking?.user?.profileImage?.path && <img
                                    src={booking?.user?.profileImage?.path}
                                    alt=""
                                    className="img-fluid"
                                  />}
                                </span>
                                {<h6 className="fw700 mb-0">
                                  {booking?.user?.firstname ? `${booking?.user?.firstname} ${booking?.user?.lastname}` : (booking?.user?.name || t("DashboardPage.GuestUser"))}</h6>}
                              </div>
                            </td>
                            <td>{booking?.teesheets && booking?.teesheets?.[0]?.teesheetIdDetail?.dateTimeISO ? (DateTime.fromISO(booking?.teesheets?.[0]?.teesheetIdDetail?.dateTimeISO, {
                              zone: mapTimeZone(booking.teesheets?.[0]?.club)
                            }).toFormat('hh:mm a, ccc, LLL dd, y')) : '-'}</td>
                            <td>{booking?.teesheets && booking?.teesheets?.[0]?.dateTime ? booking?.teesheets?.[0]?.players : 0} {t("DashboardPage.players")}, {booking?.teesheets?.[0]?.teesheetIdDetail?.holes} {t("DashboardPage.Holes")}</td>
                          </tr>))}
                        {
                          bookings.length == 0 && <div className="text-center"> {t("DashboardPage.Noreservations")}</div>
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
